import React, { useState, useEffect } from "react";
import { Mail, Inbox, Send, Check, X, RefreshCw, User, Clock, Tag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AppLayout from "../../components/AppLayout";

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all"); // all | unread | read
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const res = await api.get("/api/messages/inbox", { params: { limit: 50 } });
      setMessages(res.data.messages || []);
      setHasMore((res.data.messages || []).length === 50);
    } catch (err) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreMessages = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get("/api/messages/inbox", { params: { limit: 50, offset: messages.length } });
      const page = res.data.messages || [];
      setMessages((current) => [...current, ...page]);
      setHasMore(page.length === 50);
    } catch (err) {
      toast.error("Failed to load older messages");
    } finally {
      setLoadingMore(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await api.put(`/api/messages/${messageId}/read`);
      setMessages(messages.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
    } catch (err) {
      toast.error("Failed to mark as read");
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    if (!message.read) {
      markAsRead(message.id);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedMessage) {
      toast.error("Please enter a reply");
      return;
    }

    setSending(true);
    try {
      const response = await api.post(`/api/messages/${selectedMessage.id}/reply`, {
        message: replyText.trim(),
      });
      const updatedMessage = {
        ...selectedMessage,
        replies: [...(selectedMessage.replies || []), response.data.reply],
        replied: true,
      };
      setMessages((current) => current.map((message) => message.id === selectedMessage.id ? updatedMessage : message));
      setSelectedMessage(updatedMessage);
      toast.success("Reply sent to the Members Area");
      setReplyText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const filteredMessages = messages.filter(m => {
    if (filterStatus === "unread") return !m.read;
    if (filterStatus === "read") return m.read;
    return true;
  });

  const unreadCount = messages.filter(m => !m.read).length;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">💬 Direct Messages</h1>
            <p className="text-zinc-400">Messages from your members</p>
          </div>
          <button
            onClick={loadMessages}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition inline-flex items-center gap-2">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(113, 113, 122, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">Total Messages</div>
            <div className="text-3xl font-bold text-white">{messages.length}</div>
          </div>
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(239, 68, 68, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">Unread</div>
            <div className="text-3xl font-bold text-red-400">{unreadCount}</div>
          </div>
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(34, 197, 94, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">Replied</div>
            <div className="text-3xl font-bold text-green-400">
              {messages.filter(m => m.replied).length}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Message List */}
          <div className="lg:col-span-1 bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden">
            {/* Filter Tabs */}
            <div className="flex border-b border-white/5">
              {[
                { key: "all", label: "All" },
                { key: "unread", label: "Unread" },
                { key: "read", label: "Read" },
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setFilterStatus(filter.key)}
                  className={`flex-1 py-3 text-sm font-semibold transition ${
                    filterStatus === filter.key
                      ? "bg-purple-600/20 text-purple-300 border-b-2 border-purple-500"
                      : "text-zinc-400 hover:text-white"
                  }`}>
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Messages */}
            <div className="max-h-[600px] overflow-y-auto">
              {filteredMessages.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <Inbox size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No messages</p>
                </div>
              ) : (
                filteredMessages.map((message) => (
                  <button
                    key={message.id}
                    onClick={() => handleSelectMessage(message)}
                    className={`w-full p-4 text-left border-b border-white/5 hover:bg-zinc-800/50 transition ${
                      selectedMessage?.id === message.id ? "bg-zinc-800/70" : ""
                    } ${!message.read ? "border-l-4 border-l-purple-500" : ""}`}>
                    
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {message.member_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-white text-sm">{message.member_name}</div>
                          <div className="text-xs text-zinc-500">{message.member_email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!message.read && (
                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        )}
                        {message.replied && (
                          <Check size={14} className="text-green-400" />
                        )}
                      </div>
                    </div>

                    <div className="font-semibold text-white text-sm mb-1 truncate">
                      {message.subject}
                    </div>
                    <div className="text-xs text-zinc-400 truncate mb-2">
                      {message.message}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Clock size={12} />
                      {new Date(message.created_at).toLocaleString()}
                    </div>
                  </button>
                ))
              )}
            </div>
            {hasMore && <button type="button" onClick={loadMoreMessages} disabled={loadingMore} className="w-full border-t border-white/5 p-3 text-sm text-zinc-300 hover:bg-zinc-800/50 disabled:opacity-50">
              {loadingMore ? "Loading…" : "Load older messages"}
            </button>}
          </div>

          {/* Message Detail / Reply */}
          <div className="lg:col-span-2 bg-zinc-900/50 border border-white/5 rounded-xl">
            {selectedMessage ? (
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-white/5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {selectedMessage.member_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">{selectedMessage.member_name}</h2>
                        <p className="text-sm text-zinc-400">{selectedMessage.member_email}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-semibold ${
                          selectedMessage.member_tier === "vip"
                            ? "bg-purple-500/20 text-purple-300"
                            : "bg-pink-500/20 text-pink-300"
                        }`}>
                          {selectedMessage.member_tier.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-zinc-500 mb-1">
                        {new Date(selectedMessage.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {new Date(selectedMessage.created_at).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-2">
                    Subject: {selectedMessage.subject}
                  </h3>
                </div>

                {/* Message Body */}
                <div className="flex-1 p-6 overflow-y-auto">
                  <div className="bg-zinc-800/30 rounded-xl p-6 border border-white/5">
                    <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
                      {selectedMessage.message}
                    </p>
                  </div>
                  {(selectedMessage.replies || []).map((reply) => (
                    <div key={reply.id} className="mt-3 ml-auto max-w-[90%] rounded-xl border border-cyan-500/20 bg-cyan-950/20 p-4">
                      <p className="mb-1 text-xs font-semibold uppercase text-cyan-300">You · {new Date(reply.created_at).toLocaleString()}</p>
                      <p className="whitespace-pre-wrap text-zinc-200">{reply.message}</p>
                    </div>
                  ))}
                </div>

                {/* Reply Section */}
                <div className="p-6 border-t border-white/5">
                  <label className="block text-sm font-medium text-zinc-400 mb-3">
                    Reply in Members Area
                  </label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write a reply to this member..."
                    className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 text-white placeholder-zinc-500 focus:border-purple-500 outline-none min-h-[150px] resize-none mb-4"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={handleReply}
                      disabled={sending || !replyText.trim()}
                      className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition inline-flex items-center justify-center gap-2">
                      {sending ? (
                        <>
                          <RefreshCw size={18} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          "Send Reply"
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setSelectedMessage(null)}
                      className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg transition">
                      Close
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[600px] flex items-center justify-center">
                <div className="text-center text-zinc-500">
                  <Mail size={64} className="mx-auto mb-4 opacity-50" />
                  <p>Select a message to view and reply</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
